import "./styles/base.css";
import "./styles/map.css";
import "./styles/dashboard.css";
import "./styles/business.css";
import "./styles/mobile.css";
import { initAnalytics } from "./monitoring/analytics.js";
import { captureFeatureError, initErrorMonitoring } from "./monitoring/sentry.js";
import { initActionBinder } from "./utils/action-binder.js";
import { initClaimPage } from "./pages/claim.js";
import * as TorqoModules from "./modules.js";
import {
  createBooking,
  createCar,
  getCurrentSession,
  loadAdminData,
  loadBusinesses,
  signInUser,
  signUpUser,
} from "./data/supabase-repository.js";

const AUTH_EMAIL_RATE_LIMIT_MS = 5 * 60 * 1000;
const supabaseBridgeState = {
  authReady: false,
  user: null,
  profile: null,
  businesses: [],
  businessSource: "loading",
  lastAuthEmailSentAt: 0,
};

function authErrorMessage(error) {
  const message = String(error?.message || error || "");
  if (/email.*rate|rate.*limit|too many|429/i.test(message)) {
    return "Забагато спроб. Спробуйте трохи пізніше.";
  }
  return "Не вдалося виконати дію. Перевірте дані та спробуйте ще раз.";
}

async function initializeSupabaseState() {
  try {
    const session = await getCurrentSession();
    supabaseBridgeState.user = session.user;
    supabaseBridgeState.profile = session.profile;
    supabaseBridgeState.authReady = true;
    return session;
  } catch (error) {
    captureFeatureError("dashboard", error, { feature: "supabase_session" });
    supabaseBridgeState.authReady = true;
    return { user: null, profile: null, error };
  }
}

async function refreshBusinesses(filters = {}) {
  const result = await loadBusinesses(filters);
  supabaseBridgeState.businesses = result.data || [];
  supabaseBridgeState.businessSource = result.source || "supabase";
  return result;
}

async function handleAuth({ mode = "login", email, password, fullName = "", phone = "" } = {}) {
  try {
    const action = mode === "signup" ? signUpUser : signInUser;
    const data = await action({ email, password, fullName, phone });
    await initializeSupabaseState();
    return data;
  } catch (error) {
    throw new Error(authErrorMessage(error));
  }
}

async function handleSubmitBooking(booking) {
  if (!supabaseBridgeState.user?.id) throw new Error("Потрібно увійти заново.");
  return createBooking(supabaseBridgeState.user.id, booking);
}

async function handleSaveCar(car) {
  if (!supabaseBridgeState.user?.id) throw new Error("Потрібно увійти заново.");
  return createCar(supabaseBridgeState.user.id, car);
}

async function loadAdminPanelFromSupabase(section) {
  return loadAdminData(section);
}

function bindSupabaseActionBridge() {
  document.addEventListener("click", (event) => {
    const action = event.target?.closest?.("[data-action-click]")?.getAttribute("data-action-click") || "";
    if (action.startsWith("doAuth")) window.dispatchEvent(new CustomEvent("torqo:supabase-action", { detail: { handler: "handleAuth" } }));
    if (action.startsWith("saveCar")) window.dispatchEvent(new CustomEvent("torqo:supabase-action", { detail: { handler: "handleSaveCar" } }));
  });
}

initAnalytics();
initErrorMonitoring();
window.TorqoModules = TorqoModules;
window.TorqoSupabaseBridge = {
  AUTH_EMAIL_RATE_LIMIT_MS,
  state: supabaseBridgeState,
  initializeSupabaseState,
  refreshBusinesses,
  handleAuth,
  handleSubmitBooking,
  handleSaveCar,
  loadAdminPanelFromSupabase,
};
initActionBinder();
bindSupabaseActionBridge();
initializeSupabaseState();

if (window.location.pathname.replace(/\/+$/, "") === "/claim") {
  initClaimPage().catch((error) => {
    document.documentElement.classList.remove("torqo-booting");
    captureFeatureError("runtime", error, { module: "claim-page" });
    console.error("Torqo claim page failed to load", error);
  });
} else {
  import("./legacy-app.js").catch((error) => {
    document.documentElement.classList.remove("torqo-booting");
    captureFeatureError("runtime", error, { module: "legacy-app" });
    console.error("Torqo legacy app failed to load", error);
  });
}
